'use client';

import type { Dispatch, SetStateAction } from 'react';
import type { TFunction } from 'i18next';
import { Building2, Check, ChevronDown, Info, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  FacetCount,
  FIELD_LABEL_CLASS,
  FIELD_PLACEHOLDER_CLASS,
  FilterCheckbox,
  FilterLabel,
  INPUT_PLACEHOLDER_CLASS,
} from '../FilterControls';
import { FilterGroup } from '../FilterGroup';
import { RECHTSFORM_OPTIONS } from '../searchConstants';
import type { RechtsformOption } from '../searchConstants';
import {
  COMPANY_SIZE_HELP_EN,
  COMPANY_SIZE_OPTIONS,
  CURRENT_YEAR,
  LEGAL_STATUS_HELP_EN,
  LEGAL_STATUS_OPTIONS,
  MEMBERSHIP_OPTIONS,
  TENDER_ROLE_HELP_EN,
  TENDER_ROLE_OPTIONS,
} from '../searchSidebarOptions';

interface ProfileFilterGroupProps {
  t: TFunction<'translation', undefined>;
  profileCount: number;
  clearProfile: () => void;
  openFilterGroup: string | null;
  handleGroupOpenChange: (id: string) => (next: boolean) => void;
  groupLock: (group: string) => 'user' | 'pro' | null;
  rechtsformOpen: boolean;
  setRechtsformOpen: Dispatch<SetStateAction<boolean>>;
  selectedRechtsformen: string[];
  removeSet: (
    val: string,
    setFn: React.Dispatch<React.SetStateAction<string[]>>,
  ) => void;
  setSelectedRechtsformen: Dispatch<SetStateAction<string[]>>;
  rechtsformOptions: RechtsformOption[];
  toggleSet: (
    val: string,
    setFn: React.Dispatch<React.SetStateAction<string[]>>,
  ) => void;
  facetSum: (facet: string, values: string[]) => number | undefined;
  foundedAtFrom: string;
  setFoundedAtFrom: Dispatch<SetStateAction<string>>;
  handleFormSubmit: () => void;
  foundedAtTo: string;
  setFoundedAtTo: Dispatch<SetStateAction<string>>;
  lockOf: (control: string) => 'user' | 'pro' | null;
  membershipsOpen: boolean;
  setMembershipsOpen: Dispatch<SetStateAction<boolean>>;
  selectedMemberships: string[];
  setSelectedMemberships: Dispatch<SetStateAction<string[]>>;
  membershipOptions: { value: string; label: string }[];
  facetCount: (facet: string, value: string) => number | undefined;
  selectedLegalStatus: string[];
  setSelectedLegalStatus: Dispatch<SetStateAction<string[]>>;
  selectedCompanySize: string[];
  setSelectedCompanySize: Dispatch<SetStateAction<string[]>>;
  selectedTenderRole: string[];
  setSelectedTenderRole: Dispatch<SetStateAction<string[]>>;
  hasWebsite: boolean;
  setHasWebsite: Dispatch<SetStateAction<boolean>>;
  hasEmail: boolean;
  setHasEmail: Dispatch<SetStateAction<boolean>>;
}

export function ProfileFilterGroup({
  t,
  profileCount,
  clearProfile,
  openFilterGroup,
  handleGroupOpenChange,
  groupLock,
  rechtsformOpen,
  setRechtsformOpen,
  selectedRechtsformen,
  removeSet,
  setSelectedRechtsformen,
  rechtsformOptions,
  toggleSet,
  facetSum,
  foundedAtFrom,
  setFoundedAtFrom,
  handleFormSubmit,
  foundedAtTo,
  setFoundedAtTo,
  lockOf,
  membershipsOpen,
  setMembershipsOpen,
  selectedMemberships,
  setSelectedMemberships,
  membershipOptions,
  facetCount,
  selectedLegalStatus,
  setSelectedLegalStatus,
  selectedCompanySize,
  setSelectedCompanySize,
  selectedTenderRole,
  setSelectedTenderRole,
  hasWebsite,
  setHasWebsite,
  hasEmail,
  setHasEmail,
}: ProfileFilterGroupProps) {
  return (
    <FilterGroup
      id="profile"
      icon={Building2}
      label={t('search.sidebar.groups.profile', {
        defaultValue: 'Company profile',
      })}
      activeCount={profileCount}
      onClear={clearProfile}
      open={openFilterGroup === 'profile'}
      onOpenChange={handleGroupOpenChange('profile')}
      lock={groupLock('profile')}
    >
      {/* Rechtsform */}
      <div className="space-y-1.5">
        <Label className={FIELD_LABEL_CLASS}>
          {t('search.sidebar.rechtsform', { defaultValue: 'Legal form' })}
        </Label>
        <Popover open={rechtsformOpen} onOpenChange={setRechtsformOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              className="w-full min-h-11 lg:min-h-9 h-auto font-normal text-left justify-start py-1.5 px-3 rounded-lg relative"
            >
              <div className="flex flex-wrap gap-1 w-full">
                {selectedRechtsformen.length === 0 ? (
                  <span className={FIELD_PLACEHOLDER_CLASS}>
                    {t('search.sidebar.rechtsformPlaceholder', {
                      defaultValue: 'e.g. GmbH, AG…',
                    })}
                  </span>
                ) : (
                  selectedRechtsformen.map((code) => {
                    const opt = RECHTSFORM_OPTIONS.find(
                      (o) => o.codes.join(',') === code,
                    );
                    return (
                      <Badge
                        key={code}
                        variant="secondary"
                        className="text-xs font-normal gap-1 pr-1 border border-foreground"
                      >
                        <span className="max-w-[220px] truncate">
                          {opt?.label ?? code}
                        </span>
                        <X
                          size={12}
                          className="cursor-pointer hover:bg-muted rounded-sm"
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            removeSet(code, setSelectedRechtsformen);
                          }}
                        />
                      </Badge>
                    );
                  })
                )}
                <ChevronDown className="absolute right-2 top-2 h-4 w-4 shrink-0 opacity-50" />
              </div>
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-[var(--radix-popover-trigger-width)] p-0"
            align="start"
          >
            <Command>
              <CommandInput
                className={INPUT_PLACEHOLDER_CLASS}
                placeholder={t('search.sidebar.searchRechtsform', {
                  defaultValue: 'Search legal forms…',
                })}
              />
              <CommandList>
                <CommandEmpty>
                  {t('search.sidebar.noRechtsformFound', {
                    defaultValue: 'No legal form found.',
                  })}
                </CommandEmpty>
                <CommandGroup>
                  {rechtsformOptions.map((opt) => {
                    const key = opt.codes.join(',');
                    return (
                      <CommandItem
                        key={key}
                        value={opt.label}
                        keywords={opt.keywords}
                        onSelect={() => toggleSet(key, setSelectedRechtsformen)}
                      >
                        <Check
                          className={cn(
                            'mr-2 h-4 w-4',
                            selectedRechtsformen.includes(key)
                              ? 'opacity-100'
                              : 'opacity-0',
                          )}
                        />
                        <span className="truncate text-xs">{opt.label}</span>
                        <FacetCount
                          count={facetSum('rechtsform_code', opt.codes)}
                        />
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {/* Founded between — two 4-digit year inputs. `inputMode="numeric"`
                triggers the numeric keypad on mobile. The backend gets
                ${from}-01-01 / ${to}-12-31 in SearchPageClient. */}
      <div className="space-y-1.5">
        <Label className={FIELD_LABEL_CLASS}>
          {t('search.sidebar.foundedBetween', {
            defaultValue: 'Founded between',
          })}
        </Label>
        <div className="flex items-center gap-2">
          <Input
            type="number"
            inputMode="numeric"
            min={1800}
            max={CURRENT_YEAR}
            value={foundedAtFrom}
            onChange={(e) =>
              setFoundedAtFrom(e.target.value.replace(/\D/g, '').slice(0, 4))
            }
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleFormSubmit();
              }
            }}
            aria-label={t('search.sidebar.foundedFrom', {
              defaultValue: 'Founded from',
            })}
            placeholder="1990"
            className={`flex-1 h-11 lg:h-9 text-base lg:text-sm ${INPUT_PLACEHOLDER_CLASS}`}
          />
          <span className="text-muted-foreground text-xs shrink-0">—</span>
          <Input
            type="number"
            inputMode="numeric"
            min={1800}
            max={CURRENT_YEAR}
            value={foundedAtTo}
            onChange={(e) =>
              setFoundedAtTo(e.target.value.replace(/\D/g, '').slice(0, 4))
            }
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleFormSubmit();
              }
            }}
            aria-label={t('search.sidebar.foundedTo', {
              defaultValue: 'Founded to',
            })}
            placeholder={String(CURRENT_YEAR)}
            className={`flex-1 h-11 lg:h-9 text-base lg:text-sm ${INPUT_PLACEHOLDER_CLASS}`}
          />
        </div>
      </div>

      {/* Memberships — multi-select. Backend OR-merges. Pro-gated:
                this is the one filter that comes from data we assembled
                ourselves (the professional-registry matcher), not from a
                public register, so it is the most valuable to bulk-extract
                and the least reproducible elsewhere. */}
      <div className="space-y-1.5">
        <FilterLabel lock={lockOf('memberships')} className={FIELD_LABEL_CLASS}>
          {t('search.sidebar.memberships', { defaultValue: 'Memberships' })}
        </FilterLabel>
        <Popover
          open={lockOf('memberships') ? false : membershipsOpen}
          onOpenChange={setMembershipsOpen}
        >
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              disabled={!!lockOf('memberships')}
              className="w-full min-h-11 lg:min-h-9 h-auto font-normal text-left justify-start py-1.5 px-3 rounded-lg relative"
            >
              <div className="flex flex-wrap gap-1 w-full">
                {selectedMemberships.length === 0 ? (
                  <span className={FIELD_PLACEHOLDER_CLASS}>
                    {t('search.sidebar.membershipsPlaceholder', {
                      defaultValue: 'e.g. BaFin, Steuerberaterkammer…',
                    })}
                  </span>
                ) : (
                  selectedMemberships.map((value) => {
                    const opt = MEMBERSHIP_OPTIONS.find(
                      (o) => o.value === value,
                    );
                    return (
                      <Badge
                        key={value}
                        variant="secondary"
                        className="text-xs font-normal gap-1 pr-1 border border-foreground"
                      >
                        <span className="max-w-[220px] truncate">
                          {opt?.label ?? value}
                        </span>
                        <X
                          size={12}
                          className="cursor-pointer hover:bg-muted rounded-sm"
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            removeSet(value, setSelectedMemberships);
                          }}
                        />
                      </Badge>
                    );
                  })
                )}
                <ChevronDown className="absolute right-2 top-2 h-4 w-4 shrink-0 opacity-50" />
              </div>
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-[var(--radix-popover-trigger-width)] p-0"
            align="start"
          >
            <Command>
              <CommandList>
                <CommandGroup>
                  {membershipOptions.map((opt) => (
                    <CommandItem
                      key={opt.value}
                      value={opt.label}
                      onSelect={() =>
                        toggleSet(opt.value, setSelectedMemberships)
                      }
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4',
                          selectedMemberships.includes(opt.value)
                            ? 'opacity-100'
                            : 'opacity-0',
                        )}
                      />
                      <span className="truncate text-xs">{opt.label}</span>
                      <FacetCount count={facetCount('membership', opt.value)} />
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {/* Legal status — five disjoint buckets, resolved server-side
                from the merged Handelsregister + Insolvenzbekanntmachungen
                timeline. The ⓘ is not decoration: "in liquidation",
                "dissolved" and "insolvent" are three distinct legal states
                that all look like "not trading" from outside. */}
      <div className="space-y-1.5">
        <div className="flex items-center gap-1">
          <FilterLabel
            lock={lockOf('legalStatus')}
            className={FIELD_LABEL_CLASS}
          >
            {t('search.sidebar.legalStatus', {
              defaultValue: 'Legal status',
            })}
          </FilterLabel>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label={t('search.sidebar.legalStatusInfoLabel', {
                    defaultValue: 'What do these statuses mean?',
                  })}
                  className="inline-flex h-4 w-4 items-center justify-center rounded-full text-muted-foreground/70 hover:text-foreground"
                >
                  <Info className="h-3.5 w-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent
                side="right"
                align="start"
                className="max-w-[19rem] text-xs"
              >
                <dl className="space-y-1.5">
                  {LEGAL_STATUS_OPTIONS.map((opt) => (
                    <div key={opt.value}>
                      <dt className="font-medium">
                        {t(`search.sidebar.${opt.labelKey}`, {
                          defaultValue: opt.defaultLabel,
                        })}
                      </dt>
                      <dd className="text-muted-foreground">
                        {t(`search.sidebar.${opt.labelKey}Help`, {
                          defaultValue: LEGAL_STATUS_HELP_EN[opt.value],
                        })}
                      </dd>
                    </div>
                  ))}
                </dl>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <div className="space-y-0.5">
          {LEGAL_STATUS_OPTIONS.map((opt) => (
            <FilterCheckbox
              key={opt.value}
              checked={selectedLegalStatus.includes(opt.value)}
              onCheckedChange={(v) =>
                setSelectedLegalStatus((prev) =>
                  v
                    ? [...prev, opt.value]
                    : prev.filter((x) => x !== opt.value),
                )
              }
              lock={lockOf('legalStatus')}
              label={t(`search.sidebar.${opt.labelKey}`, {
                defaultValue: opt.defaultLabel,
              })}
              count={facetCount('legal_status', opt.value)}
            />
          ))}
        </div>
      </div>

      {/* Company size — the § 267 HGB classes. Derived from what the
                company was legally required to publish, not from a figure it
                filed: withholding the profit-and-loss statement is a relief
                only small companies may claim. So this reaches ~761k
                companies that have no turnover on file at all, which is the
                whole point of it. Ticking every box still filters. */}
      <div className="space-y-1.5">
        <div className="flex items-center gap-1">
          <FilterLabel
            lock={lockOf('companySize')}
            className={FIELD_LABEL_CLASS}
          >
            {t('search.sidebar.companySize', {
              defaultValue: 'Company size',
            })}
          </FilterLabel>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label={t('search.sidebar.companySizeInfoLabel', {
                    defaultValue: 'How is company size determined?',
                  })}
                  className="inline-flex h-4 w-4 items-center justify-center rounded-full text-muted-foreground/70 hover:text-foreground"
                >
                  <Info className="h-3.5 w-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent
                side="right"
                align="start"
                className="max-w-[19rem] text-xs"
              >
                <dl className="space-y-1.5">
                  {COMPANY_SIZE_OPTIONS.map((opt) => (
                    <div key={opt.value}>
                      <dt className="font-medium">
                        {t(`search.sidebar.${opt.labelKey}`, {
                          defaultValue: opt.defaultLabel,
                        })}
                      </dt>
                      <dd className="text-muted-foreground">
                        {t(`search.sidebar.${opt.labelKey}Help`, {
                          defaultValue: COMPANY_SIZE_HELP_EN[opt.value],
                        })}
                      </dd>
                    </div>
                  ))}
                </dl>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <div className="space-y-0.5">
          {COMPANY_SIZE_OPTIONS.map((opt) => (
            <FilterCheckbox
              key={opt.value}
              checked={selectedCompanySize.includes(opt.value)}
              onCheckedChange={(v) =>
                setSelectedCompanySize((prev) =>
                  v
                    ? [...prev, opt.value]
                    : prev.filter((x) => x !== opt.value),
                )
              }
              lock={lockOf('companySize')}
              label={t(`search.sidebar.${opt.labelKey}`, {
                defaultValue: opt.defaultLabel,
              })}
              count={facetCount('company_size', opt.value)}
            />
          ))}
        </div>
      </div>

      {/* Public procurement — two overlapping roles from EU TED
                notices. Unlike the legal-status buckets above, ticking both
                is a real filter (~1% of the register appears in TED at all),
                so there is no "select everything = no filter" shortcut here.
                The ⓘ carries the coverage caveat: TED data starts 2025. */}
      <div className="space-y-1.5">
        <div className="flex items-center gap-1">
          <FilterLabel
            lock={lockOf('tenderRole')}
            className={FIELD_LABEL_CLASS}
          >
            {t('search.sidebar.tenderRole', {
              defaultValue: 'Public procurement',
            })}
          </FilterLabel>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label={t('search.sidebar.tenderRoleInfoLabel', {
                    defaultValue: 'What do these roles mean?',
                  })}
                  className="inline-flex h-4 w-4 items-center justify-center rounded-full text-muted-foreground/70 hover:text-foreground"
                >
                  <Info className="h-3.5 w-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent
                side="right"
                align="start"
                className="max-w-[19rem] text-xs"
              >
                <dl className="space-y-1.5">
                  {TENDER_ROLE_OPTIONS.map((opt) => (
                    <div key={opt.value}>
                      <dt className="font-medium">
                        {t(`search.sidebar.${opt.labelKey}`, {
                          defaultValue: opt.defaultLabel,
                        })}
                      </dt>
                      <dd className="text-muted-foreground">
                        {t(`search.sidebar.${opt.labelKey}Help`, {
                          defaultValue: TENDER_ROLE_HELP_EN[opt.value],
                        })}
                      </dd>
                    </div>
                  ))}
                  <div>
                    <dd className="text-muted-foreground">
                      {t('search.sidebar.tenderRoleCoverage', {
                        defaultValue:
                          'Source: EU TED. Covers notices published from January 2025 onwards.',
                      })}
                    </dd>
                  </div>
                </dl>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <div className="space-y-0.5">
          {TENDER_ROLE_OPTIONS.map((opt) => (
            <FilterCheckbox
              key={opt.value}
              checked={selectedTenderRole.includes(opt.value)}
              onCheckedChange={(v) =>
                setSelectedTenderRole((prev) =>
                  v
                    ? [...prev, opt.value]
                    : prev.filter((x) => x !== opt.value),
                )
              }
              lock={lockOf('tenderRole')}
              label={t(`search.sidebar.${opt.labelKey}`, {
                defaultValue: opt.defaultLabel,
              })}
              count={facetCount('tender_role', opt.value)}
            />
          ))}
        </div>
      </div>

      {/* Contact — boolean checkboxes. Headed rather than loose: they
                sit directly under the legal-status checkboxes and without a
                label of their own the two sets read as one list, so "Has
                Email" looked like another lifecycle state. We deliberately
                don't expose a "false" mode (only-without-X is rarely useful);
                unchecked = no filter applied. */}
      <div className="space-y-1.5">
        <FilterLabel
          lock={lockOf('hasWebsite') && lockOf('hasEmail')}
          className={FIELD_LABEL_CLASS}
        >
          {t('search.sidebar.contact', { defaultValue: 'Contact' })}
        </FilterLabel>
        <div className="space-y-0.5">
          <FilterCheckbox
            checked={hasWebsite}
            onCheckedChange={setHasWebsite}
            lock={lockOf('hasWebsite')}
            label={t('search.sidebar.hasWebsite', {
              defaultValue: 'Has Website',
            })}
            count={facetCount('has_website', 'true')}
          />
          <FilterCheckbox
            checked={hasEmail}
            onCheckedChange={setHasEmail}
            lock={lockOf('hasEmail')}
            label={t('search.sidebar.hasEmail', {
              defaultValue: 'Has Email',
            })}
            count={facetCount('has_email', 'true')}
          />
        </div>
      </div>
    </FilterGroup>
  );
}
